/**
 * CVSearch - DEPRECATED Simple Text-based CV Search
 * Route: /search
 *
 * Old search page with SearchHeader + ResultsList + CVViewer.
 * Superseded by InterestedFeed.tsx at /candidate-search.
 * Kept for backwards compatibility - not linked from main navigation.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SearchHeader from '../components/CVSearch/SearchHeader';
import PassportViewer from '../components/Passport/PassportViewer';
import PassportCover from '../components/Passport/PassportCover';
import PassportCoverSkeleton from '../components/Passport/PassportCoverSkeleton';
import PassportReactionBar from '../components/Passport/PassportReactionBar';
import { ENABLE_PASSPORT_REACTIONS, getMyReactionsAllByCv, type ReactionKey } from '../api/passportReactions';
import { getCvPassport, type CvPassport } from '../api/passport';
import { ENABLE_SAVED_PASSPORTS } from '../config/features';
import AuthModal from '../components/Auth/AuthModal';
import { useAuthModal } from '../hooks/useAuthModal';
import { Sparkles, ArrowLeft, ArrowRight, Upload, X, Search, IdCard, Eye } from 'lucide-react';
import RateLimitNotification from '../components/RateLimitNotification';
import { useAuth } from '../hooks/useAuth';
import { showToast, handleApiError, RateLimitInfo } from '../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import RecruiterExplorerGate from '../components/RecruiterExplorerGate';
import { WeeklyTrendsChip, WeeklyTrendsPanel } from '../components/WeeklyTrends';

// Feature flag - set to true to re-enable Vee Helper
interface CVResult {
  cv_id: string;
  score: number;
  keywords: string[];
  preview: string;
  cv_language?: string; // "en" or "de"
  career_status?: string; // Recruiter-visibility only; shown on Explore regardless
  // Simple search: only keywords, no user preferences
}

interface CVImages {
  cv_id: string;
  images: string[];
  image_count: number;
}

interface CVKeywords {
  cv_id: string;
  keywords: string[];
  sections: Record<string, string[]>;
}

interface RealTalkData {
  real_talk_sentence: string | null;
  real_talk_tags: string[] | null;
  is_contactable: boolean;
}

interface CVSearchProps {
  initialCV?: string | null;
}

const CVSearch: React.FC<CVSearchProps> = ({ initialCV }) => {
  const navigate = useNavigate();
  const { user, apiCall, isLoading: authLoading } = useAuth();
  const { showAuthModal, setShowAuthModal, requireAuth, isSignedIn } = useAuthModal();
  const { t } = useTranslation('common');
  const [searchResults, setSearchResults] = useState<CVResult[]>([]);
  const [allResults, setAllResults] = useState<CVResult[]>([]); // Unfiltered results from backend
  const [selectedCV, setSelectedCV] = useState<string | null>(null);
  const [cvImages, setCvImages] = useState<Record<string, CVImages>>({});
  const [cvKeywords, setCvKeywords] = useState<Record<string, CVKeywords>>({});
  const [cvRealTalk, setCvRealTalk] = useState<Record<string, RealTalkData>>({});
  const [cvPassport, setCvPassport] = useState<Record<string, CvPassport>>({});
  // The viewer's own reactions, keyed by target cv_id, seeded once so feed cards
  // show what they already reacted (and can un-react) without N per-card calls.
  const [myReactions, setMyReactions] = useState<Record<string, ReactionKey[]>>({});
  // Tracks which CVs have *resolved* their passport fetch (loaded or none) so the
  // cover can show station/meta skeletons until then instead of an empty card.
  const [passportDone, setPassportDone] = useState<Record<string, boolean>>({});
  const [isBlocked, setIsBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  
  // Saved CVs state
  const [savedCVIds, setSavedCVIds] = useState<Set<string>>(new Set());
  const [currentSearchQuery, setCurrentSearchQuery] = useState<string>('');
  
  // Weekly Trends state
  const [showTrendsPanel, setShowTrendsPanel] = useState(false);
  
  // Show more state
  const [displayCount, setDisplayCount] = useState(10);

  // Mobile search scroll state
  const [searchScrollCount, setSearchScrollCount] = useState(3);
  const searchScrollCountRef = useRef(searchScrollCount);
  searchScrollCountRef.current = searchScrollCount;
  const searchScrollObserverRef = useRef<IntersectionObserver | null>(null);
  const searchViewObserverRef = useRef<IntersectionObserver | null>(null);
  const searchViewedRef = useRef(new Set<string>());
  const searchOpenedRef = useRef(new Set<string>());
  const searchPreloadedRef = useRef(new Set<string>());

  // Feed states
  const [feedTab, setFeedTab] = useState<'foryou' | 'discover'>(() => {
    const saved = localStorage.getItem('cevee_feed_tab');
    if (saved === 'foryou' || saved === 'discover') return saved;
    return 'foryou';
  });
  const [feedCandidates, setFeedCandidates] = useState<CVResult[]>([]);
  const [discoverCandidates, setDiscoverCandidates] = useState<CVResult[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedDisplayCount, setFeedDisplayCount] = useState(1);
  const [hasCv, setHasCv] = useState<boolean | null>(null);
  const [showCvNudge, setShowCvNudge] = useState(false);
  const feedViewedRef = useRef(new Set<string>());
  const feedOpenedRef = useRef(new Set<string>());
  const feedPreloadedRef = useRef(new Set<string>());
  const passportLoadedRef = useRef(new Set<string>());
  const feedScrollObserverRef = useRef<IntersectionObserver | null>(null);
  const feedViewObserverRef = useRef<IntersectionObserver | null>(null);
  const feedDisplayCountRef = useRef(feedDisplayCount);
  feedDisplayCountRef.current = feedDisplayCount;
  const feedCandidatesRef = useRef(feedCandidates);
  feedCandidatesRef.current = feedCandidates;
  const discoverCandidatesRef = useRef(discoverCandidates);
  discoverCandidatesRef.current = discoverCandidates;
  const feedTabRef = useRef(feedTab);
  feedTabRef.current = feedTab;

  const showFeed = allResults.length === 0 && !isLoading;
  const currentFeed = feedTab === 'foryou' ? feedCandidates : discoverCandidates;
  
  const handleTrendsClick = () => {
    if (!isSignedIn) {
      // Show auth modal for logged out users
      setShowAuthModal(true);
      return;
    }
    setShowTrendsPanel(prev => !prev);
  };
  // Load language filter from localStorage or use default (both selected)
  const [languageFilter, setLanguageFilter] = useState<{ de: boolean; en: boolean }>(() => {
    const saved = localStorage.getItem('cevee_lang_filter');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { de: true, en: true };
      }
    }
    return { de: true, en: true };
  });

  // Persist language filter to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cevee_lang_filter', JSON.stringify(languageFilter));
  }, [languageFilter]);

  // Seed the viewer's own reactions once so feed cards reflect what they already
  // reacted to (and can withdraw). One batched call instead of per-card lookups.
  useEffect(() => {
    if (!ENABLE_PASSPORT_REACTIONS || !isSignedIn) return;
    let cancelled = false;
    getMyReactionsAllByCv(apiCall)
      .then((m) => { if (!cancelled) setMyReactions(m); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isSignedIn, apiCall]);

  // Helper function to filter results by language (memoized for stability).
  // NOTE: 'invisible' is only a recruiter-visibility setting — it must NOT hide
  // a CV on Explore (explorer-to-explorer discovery), so we do not filter it here.
  const applyLanguageFilter = useCallback((results: CVResult[], filter: { de: boolean; en: boolean }): CVResult[] => {
    // If both unselected → show empty (user needs to select at least one)
    if (!filter.de && !filter.en) {
      return [];
    }
    
    // If both selected → show all
    if (filter.de && filter.en) {
      return results;
    }
    
    // Filter by selected language(s)
    return results.filter((result: CVResult) => {
      if (filter.de && result.cv_language === 'de') return true;
      if (filter.en && result.cv_language === 'en') return true;
      return false;
    });
  }, []);

  // Compute filtered results (for show more logic)
  const filteredResultsCount = React.useMemo(() => {
    if (allResults.length === 0) return 0;
    return applyLanguageFilter(allResults, languageFilter).length;
  }, [allResults, languageFilter, applyLanguageFilter]);

  // Re-filter results when language filter or displayCount changes
  useEffect(() => {
    if (allResults.length > 0) {
      const filteredResults = applyLanguageFilter(allResults, languageFilter);
      const visibleResults = filteredResults.slice(0, displayCount);
      setSearchResults(visibleResults);
    }
  }, [languageFilter, allResults, applyLanguageFilter, displayCount]);

  // Show more handler
  const handleShowMore = () => {
    setDisplayCount(prev => prev + 5);
  };

  const loadFeed = async (personalized: boolean, attempt = 0): Promise<void> => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const resp = await apiCall(`${apiBaseUrl}/api/v1/companies/cv-feed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personalized, top_k: 10 }),
      });
      if (!resp.ok) throw new Error(`API error ${resp.status}`);
      const data = await resp.json();

      if (data.has_cv === false && personalized) {
        setHasCv(false);
        return;
      }
      if (data.has_cv !== undefined) setHasCv(data.has_cv);

      const all = data.candidates || [];
      if (personalized) {
        setFeedCandidates(all);
      } else {
        setDiscoverCandidates([...all].sort(() => Math.random() - 0.5));
      }
    } catch (e) {
      console.error('Feed load failed:', e);
      if (attempt < 2) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
        return loadFeed(personalized, attempt + 1);
      }
    }
  };

  // Check if user is recruiter, load saved CVs, and load feed
  useEffect(() => {
    const loadUserData = async () => {
      if (isSignedIn && user) {
        try {
          const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
          const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);
          if (response.ok) {
            const profile = await response.json();
            if (profile.is_recruiter) {
              setIsBlocked(true);
              return;
            }
            if (profile.saved_cvs && Array.isArray(profile.saved_cvs)) {
              const savedIds = new Set<string>(profile.saved_cvs.map((cv: { cv_id: string }) => cv.cv_id));
              setSavedCVIds(savedIds);
            }
            const userHasCv = !!profile.cv_id;
            setHasCv(userHasCv);
            // Default to "Für Dich" if CV exists, "Entdecken" if not (unless user already chose)
            if (!localStorage.getItem('cevee_feed_tab')) {
              setFeedTab(userHasCv ? 'foryou' : 'discover');
            } else if (!userHasCv && feedTabRef.current === 'foryou') {
              setFeedTab('discover');
            }
            setFeedLoading(true);
            await Promise.all([loadFeed(true), loadFeed(false)]);
            setFeedLoading(false);
          }
        } catch (error) {
          console.error('Failed to load profile:', error);
          setFeedLoading(false);
        }
      }
    };
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user?.userId]);

  const handleSearch = async (query: string) => {
    // Check auth FIRST
    if (!requireAuth()) {
      return; // Auth modal will be shown
    }

    if (isBlocked) return;
    
    setIsLoading(true);
    setRateLimitInfo(null); // Clear previous rate limit info
    setCurrentSearchQuery(query); // Store for saving CVs
    
    try {
      const response = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/exploration/simple`, {
        method: 'POST',
        body: JSON.stringify({
          query
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.debug('🔍 EXPLORE DEBUG: Backend response:', data);
        console.debug('🔍 EXPLORE DEBUG: First result keywords:', data.results?.[0]?.keywords);
        console.debug('🔍 EXPLORE DEBUG: Query language:', data.query_language);
        
        // Store all results (unfiltered) - useEffect will handle filtering
        // DON'T set searchResults here directly - let useEffect handle it to avoid double-update bug
        const results = data.results || [];
        setAllResults(results);
        console.debug('🔍 EXPLORE DEBUG: Backend returned', results.length, 'results');
        
        // Clear previous CV data for fresh search
        setCvImages({});
        setCvKeywords({});
        setCvRealTalk({});
        setSelectedCV(null);
        setDisplayCount(10);
      } else {
        // Handle API errors with proper error handler
        const apiError = await handleApiError(response);
        
        if (apiError.status === 429 && apiError.rateLimitInfo) {
          // Show rate limit notification component
          setRateLimitInfo(apiError.rateLimitInfo);
        } else {
          // Show toast for other errors
          showToast(apiError.message, 'error', apiError.retryAfterSeconds ? apiError.retryAfterSeconds * 1000 : 5000);
        }
      }
    } catch (error: any) {
      console.error('Search failed:', error);
      showToast(t('explore.searchErrorConnection'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Save/Unsave CV handlers
  const handleSaveCV = async (cvId: string) => {
    if (!isSignedIn || !currentSearchQuery) return;
    
    try {
      const response = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/profiles/me/saved-cvs`, {
        method: 'POST',
        body: JSON.stringify({
          cv_id: cvId,
          prompt: currentSearchQuery
        }),
      });
      
      if (response.ok) {
        setSavedCVIds(prev => {
          const newSet = new Set(prev);
          newSet.add(cvId);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Failed to save CV:', error);
    }
  };

  const handleUnsaveCV = async (cvId: string) => {
    if (!isSignedIn) return;
    
    try {
      const response = await apiCall(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1/profiles/me/saved-cvs/${cvId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        setSavedCVIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(cvId);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Failed to unsave CV:', error);
    }
  };

  const handleToggleSave = async (cvId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger card click
    if (savedCVIds.has(cvId)) {
      await handleUnsaveCV(cvId);
    } else {
      await handleSaveCV(cvId);
    }
  };

  const loadCVData = async (cvId: string): Promise<boolean> => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      let imagesOk = false;
      
      const imagesResponse = await apiCall(`${apiBaseUrl}/api/v1/cv/${cvId}/images`);
      if (imagesResponse.ok) {
        const imagesData = await imagesResponse.json();
        setCvImages(prev => ({ ...prev, [cvId]: imagesData }));
        imagesOk = !!imagesData?.images?.length;
      }

      const keywordsResponse = await apiCall(`${apiBaseUrl}/api/v1/cv/${cvId}/content?include_keywords=false`);
      if (keywordsResponse.ok) {
        const contentData = await keywordsResponse.json();
        setCvKeywords(prev => ({
          ...prev,
          [cvId]: {
            cv_id: cvId,
            keywords: contentData.metadata?.keywords || [],
            sections: contentData.metadata?.sections_keywords || {}
          }
        }));
      }

      const publicProfileResponse = await apiCall(`${apiBaseUrl}/api/v1/profiles/by-cv-id/${cvId}/public`);
      if (publicProfileResponse.ok) {
        const publicData = await publicProfileResponse.json();
        setCvRealTalk(prev => ({
          ...prev,
          [cvId]: {
            real_talk_sentence: publicData.real_talk_sentence || null,
            real_talk_tags: publicData.real_talk_tags || null,
            is_contactable: publicData.is_contactable || false
          }
        }));
      }

      return imagesOk;
    } catch (error: any) {
      console.error(`Failed to load data for CV ${cvId}:`, error);
      return false;
    }
  };

  const trackCvEvent = async (cvId: string, kind: 'impression' | 'open') => {
    if (!isSignedIn) return;
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      await apiCall(`${apiBaseUrl}/api/v1/cv/${cvId}/track-event?kind=${kind}`, { method: 'POST' });
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!initialCV || authLoading || !isSignedIn) return;
    let cancelled = false;
    try {
      const cvId = atob(initialCV);
      if (cvId && /^[0-9a-f-]{36}$/i.test(cvId)) {
        setSelectedCV(cvId);
        if (!feedOpenedRef.current.has(cvId) && !searchOpenedRef.current.has(cvId)) {
          feedOpenedRef.current.add(cvId);
          searchOpenedRef.current.add(cvId);
          trackCvEvent(cvId, 'open');
        }
        const loadWithRetry = async (attempt: number) => {
          const ok = await loadCVData(cvId);
          if (cancelled) return;
          if (!ok && attempt < 2) {
            setTimeout(() => loadWithRetry(attempt + 1), 2000);
          } else if (!ok) {
            navigate('/?tab=explore', { replace: true });
          }
        };
        loadWithRetry(0);
      } else {
        navigate('/?tab=explore', { replace: true });
      }
    } catch {
      navigate('/?tab=explore', { replace: true });
    }
    return () => { cancelled = true; };
  }, [initialCV, authLoading, isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref to the passport detail container so we can scroll it into view when
  // opening from the list (but NOT on prev/next — see handleCVSelect).
  const detailRef = useRef<HTMLDivElement>(null);

  const closeDetail = useCallback(() => {
    setSelectedCV(null);
    const params = new URLSearchParams(window.location.search);
    if (params.has('cv')) {
      params.delete('cv');
      window.history.replaceState({}, '', `?${params.toString()}`);
    }
  }, []);

  // Device/browser back button: when the URL no longer carries a cv, close the
  // open passport instead of leaving the page.
  useEffect(() => {
    const onPop = () => {
      const params = new URLSearchParams(window.location.search);
      if (!params.get('cv')) setSelectedCV(null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const handleCVSelect = (cvId: string, scrollIntoView: boolean = true) => {
    setSelectedCV(cvId);
    // Opening from the list: bring the detail to the top of the viewport for a
    // clean passport view. On prev/next (scrollIntoView=false) keep the scroll
    // position so browsing candidates doesn't jump around.
    if (scrollIntoView) {
      requestAnimationFrame(() => {
        detailRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    }
    const params = new URLSearchParams(window.location.search);
    const hadCv = params.has('cv');
    params.set('cv', btoa(cvId));
    // Fresh open → push a history entry so the device/browser back button closes
    // the passport. Switching candidates (cv already open) → replace, no spam.
    if (hadCv) {
      window.history.replaceState({}, '', `?${params.toString()}`);
    } else {
      window.history.pushState({}, '', `?${params.toString()}`);
    }
    if (!feedOpenedRef.current.has(cvId) && !searchOpenedRef.current.has(cvId)) {
      feedOpenedRef.current.add(cvId);
      searchOpenedRef.current.add(cvId);
      trackCvEvent(cvId, 'open');
    }
    if (!cvImages[cvId] || !cvKeywords[cvId]) {
      loadCVData(cvId);
    }
    // Preload next 2 (no engagement tracking)
    const idx = searchResults.findIndex((r) => r.cv_id === cvId);
    for (let i = 1; i <= 2; i++) {
      if (idx + i < searchResults.length) {
        const nextId = searchResults[idx + i].cv_id;
        if (!cvImages[nextId] || !cvKeywords[nextId]) loadCVData(nextId);
      }
    }
  };

  // Feed: preload CV data for visible + next 2 items
  const isSignedInRef = useRef(isSignedIn);
  isSignedInRef.current = isSignedIn;

  useEffect(() => {
    if (!showFeed || !isSignedIn) return;
    const feed = feedTabRef.current === 'foryou' ? feedCandidatesRef.current : discoverCandidatesRef.current;
    const toPreload = feed.slice(0, feedDisplayCount + 1);
    toPreload.forEach(cv => {
      if (!feedPreloadedRef.current.has(cv.cv_id)) {
        feedPreloadedRef.current.add(cv.cv_id);
        loadCVData(cv.cv_id);
      }
      // Passport (cached server-side) → powers the cover's Stations journey.
      if (!passportLoadedRef.current.has(cv.cv_id)) {
        passportLoadedRef.current.add(cv.cv_id);
        getCvPassport(apiCall, cv.cv_id)
          .then(p => { if (p) setCvPassport(prev => ({ ...prev, [cv.cv_id]: p })); })
          .catch(() => {})
          .finally(() => setPassportDone(prev => ({ ...prev, [cv.cv_id]: true })));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedDisplayCount, feedTab, feedCandidates.length, discoverCandidates.length, showFeed, isSignedIn]);

  // Feed: view tracking observer (only tracks when signed in)
  useEffect(() => {
    feedViewObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (!isSignedInRef.current) return;
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const cvId = entry.target.getAttribute('data-cv-id');
            if (cvId && !feedViewedRef.current.has(cvId)) {
              feedViewedRef.current.add(cvId);
              trackCvEvent(cvId, 'impression');
            }
          }
        });
      },
      { threshold: 0.3 }
    );
    return () => feedViewObserverRef.current?.disconnect();
  }, []);

  // Feed: infinite scroll sentinel (callback ref)
  const feedSentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (feedScrollObserverRef.current) feedScrollObserverRef.current.disconnect();
    if (!node) return;
    feedScrollObserverRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const feed = feedTabRef.current === 'foryou' ? feedCandidatesRef.current : discoverCandidatesRef.current;
          if (feedDisplayCountRef.current < feed.length) {
            setFeedDisplayCount(prev => prev + 1);
            // IntersectionObserver only fires on transitions. When the sentinel
            // stays continuously in view (e.g. the next card's image is already
            // cached so the node never unmounts/remounts) it would otherwise
            // load just one card and stall. Re-observe so it re-evaluates and
            // keeps advancing until the sentinel is pushed out of view.
            const obs = feedScrollObserverRef.current;
            if (obs) {
              obs.unobserve(entry.target);
              requestAnimationFrame(() => {
                if (feedScrollObserverRef.current === obs) obs.observe(entry.target);
              });
            }
          }
        }
      },
      { rootMargin: '300px' }
    );
    feedScrollObserverRef.current.observe(node);
  }, []);

  // Feed: observe new feed items for view tracking
  const feedItemRef = useCallback((el: HTMLDivElement | null) => {
    if (el && feedViewObserverRef.current) {
      feedViewObserverRef.current.observe(el);
    }
  }, []);

  // Search scroll: view tracking observer (mobile continuous scroll)
  // Stable observer - never re-created, refs re-attach via searchItemRef
  useEffect(() => {
    searchViewObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (!isSignedInRef.current) return;
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const cvId = entry.target.getAttribute('data-cv-id');
            if (cvId && !searchViewedRef.current.has(cvId)) {
              searchViewedRef.current.add(cvId);
              trackCvEvent(cvId, 'impression');
            }
          }
        });
      },
      { threshold: 0.3 }
    );
    return () => searchViewObserverRef.current?.disconnect();
  }, []);

  // Search scroll: infinite scroll sentinel (mobile)
  const searchResultsLenRef = useRef(searchResults.length);
  searchResultsLenRef.current = searchResults.length;
  const searchSentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (searchScrollObserverRef.current) searchScrollObserverRef.current.disconnect();
    if (!node) return;
    searchScrollObserverRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && searchScrollCountRef.current < searchResultsLenRef.current) {
          setSearchScrollCount(prev => prev + 2);
        }
      },
      { rootMargin: '200px' }
    );
    searchScrollObserverRef.current.observe(node);
  }, []);

  // Search scroll: observe items for view tracking
  // Stable callback - observer is stable, React handles ref lifecycle on re-render
  const searchItemRef = useCallback((el: HTMLDivElement | null) => {
    if (el && searchViewObserverRef.current) {
      searchViewObserverRef.current.observe(el);
    }
  }, []);

  // Search scroll: preload CV data for visible + next items (mobile)
  // Depends on searchResults (array ref) not .length - ensures re-fire on every new search
  useEffect(() => {
    if (showFeed || !isSignedIn || searchResults.length === 0) return;
    const toPreload = searchResults.slice(0, searchScrollCount + 1);
    toPreload.forEach(cv => {
      if (!searchPreloadedRef.current.has(cv.cv_id)) {
        searchPreloadedRef.current.add(cv.cv_id);
        loadCVData(cv.cv_id);
      }
      // Passport (cached server-side) → powers the result card's Stations journey.
      if (!passportLoadedRef.current.has(cv.cv_id)) {
        passportLoadedRef.current.add(cv.cv_id);
        getCvPassport(apiCall, cv.cv_id)
          .then(p => { if (p) setCvPassport(prev => ({ ...prev, [cv.cv_id]: p })); })
          .catch(() => {})
          .finally(() => setPassportDone(prev => ({ ...prev, [cv.cv_id]: true })));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchScrollCount, searchResults, showFeed, isSignedIn]);

  // Reset search scroll state when new search happens
  useEffect(() => {
    setSearchScrollCount(3);
    searchViewedRef.current = new Set();
    searchOpenedRef.current = new Set();
    searchPreloadedRef.current = new Set();
    if (searchScrollObserverRef.current) searchScrollObserverRef.current.disconnect();
  }, [allResults]);

  // Feed: reset display count when switching tabs
  const handleFeedTabSwitch = (tab: 'foryou' | 'discover') => {
    if (tab === 'foryou' && hasCv === false) {
      setShowCvNudge(true);
      return;
    }
    setFeedTab(tab);
    localStorage.setItem('cevee_feed_tab', tab);
    setFeedDisplayCount(1);
  };

  const clearSearch = () => {
    window.location.href = '/?tab=explore';
  };

  // CV navigation
  const cvIdx = selectedCV ? searchResults.findIndex((r) => r.cv_id === selectedCV) : -1;
  const hasPrevCV = cvIdx > 0;
  const hasNextCV = cvIdx >= 0 && cvIdx < searchResults.length - 1;

  const navigateCV = useCallback((dir: 'prev' | 'next') => {
    const idx = searchResults.findIndex((r) => r.cv_id === selectedCV);
    const nextIdx = dir === 'prev' ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= searchResults.length) return;
    const nextId = searchResults[nextIdx].cv_id;
    handleCVSelect(nextId, false);
  }, [searchResults, selectedCV]); // eslint-disable-line react-hooks/exhaustive-deps

  // Feed navigation (Passport overlay opened from a cover card).
  const feedIdx = selectedCV ? currentFeed.findIndex((c) => c.cv_id === selectedCV) : -1;
  const navigateFeed = useCallback((dir: 'prev' | 'next') => {
    const idx = currentFeed.findIndex((c) => c.cv_id === selectedCV);
    const nextIdx = dir === 'prev' ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= currentFeed.length) return;
    handleCVSelect(currentFeed[nextIdx].cv_id, false);
  }, [currentFeed, selectedCV]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedCV) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigateCV('prev'); }
      if (e.key === 'ArrowRight') { e.preventDefault(); navigateCV('next'); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedCV, navigateCV]);

  if (isBlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <RecruiterExplorerGate />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 relative flex flex-col">
      {/* Rate Limit Notification */}
      {rateLimitInfo && (
        <RateLimitNotification 
          rateLimitInfo={rateLimitInfo}
          feature="cv_search"
          onClose={() => setRateLimitInfo(null)}
        />
      )}
      
      {/* Search Header. World switching (Community / Creators / Companies) now
          lives in the shared Explore nav in Home2, so no pillars here. */}
      <SearchHeader 
        onSearch={handleSearch} 
        isLoading={isLoading}
      />
      
      {/* Main Content */}
      {showFeed ? (
        /* ─── FEED VIEW ─── */
        <div className="flex-1 w-full max-w-4xl mx-auto px-4 py-4">
          {selectedCV ? (
            /* Inline passport detail — stays under the search box; Back (and the
               device/browser back button) return to the list. */
            <div
              ref={detailRef}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden scroll-mt-3 mb-28 max-w-2xl mx-auto w-full"
            >
              <PassportViewer
                cvId={selectedCV}
                fillHeight={false}
                imagesOverride={cvImages[selectedCV]?.images || null}
                passportOverride={cvPassport[selectedCV]}
                realTalkOverride={cvRealTalk[selectedCV] ? {
                  cv_id: selectedCV,
                  real_talk_sentence: cvRealTalk[selectedCV].real_talk_sentence,
                  real_talk_tags: cvRealTalk[selectedCV].real_talk_tags,
                  is_contactable: cvRealTalk[selectedCV].is_contactable,
                } : null}
                onBack={closeDetail}
                isSaved={ENABLE_SAVED_PASSPORTS && savedCVIds.has(selectedCV)}
                onToggleSave={ENABLE_SAVED_PASSPORTS ? handleToggleSave : undefined}
                enableReactions={ENABLE_PASSPORT_REACTIONS}
                navSlot={feedIdx >= 0 && currentFeed.length > 1 ? (
                  <div className="flex items-center justify-between px-3 py-2 border-b border-pink-100 bg-pink-50/70">
                    <button
                      onClick={() => navigateFeed('prev')}
                      disabled={feedIdx <= 0}
                      className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-all ${feedIdx > 0 ? 'text-pink-600 hover:bg-pink-100' : 'text-pink-200 cursor-default'}`}
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Prev
                    </button>
                    <span className="text-xs text-pink-400 font-semibold">{feedIdx + 1} / {currentFeed.length}</span>
                    <button
                      onClick={() => navigateFeed('next')}
                      disabled={feedIdx >= currentFeed.length - 1}
                      className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-all ${feedIdx < currentFeed.length - 1 ? 'text-pink-600 hover:bg-pink-100' : 'text-pink-200 cursor-default'}`}
                    >
                      Next <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : undefined}
              />
            </div>
          ) : (
          <>
          {/* Feed tabs — subtle segmented control (matches header language / view-mode tone) */}
          <div className="mb-3 sm:mb-5">
            <div className="inline-flex items-center rounded-full bg-slate-100/90 p-0.5 text-[11px] sm:text-xs font-semibold ring-1 ring-slate-200/60">
              <button
                onClick={() => handleFeedTabSwitch('foryou')}
                aria-pressed={feedTab === 'foryou'}
                className={`rounded-full px-2.5 py-1 sm:px-3 sm:py-1.5 transition-all whitespace-nowrap ${
                  feedTab === 'foryou'
                    ? 'bg-white/95 text-slate-800 shadow-sm backdrop-blur-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t('explore.feed.forYouTab')}
              </button>
              <button
                onClick={() => handleFeedTabSwitch('discover')}
                aria-pressed={feedTab === 'discover'}
                className={`rounded-full px-2.5 py-1 sm:px-3 sm:py-1.5 transition-all whitespace-nowrap ${
                  feedTab === 'discover'
                    ? 'bg-white/95 text-slate-800 shadow-sm backdrop-blur-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t('explore.feed.discoverTab')}
              </button>
            </div>
          </div>

          {/* CV Nudge Modal */}
          <AnimatePresence>
            {showCvNudge && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
                onClick={() => setShowCvNudge(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white rounded-2xl p-5 sm:p-6 max-w-sm w-full shadow-xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Sparkles className="w-5 h-5 sm:w-7 sm:h-7 text-pink-500" />
                    </div>
                    <h3 className="text-base sm:text-xl font-bold text-slate-800 mb-1.5">
                      {t('explore.feed.cvNudgeTitle')}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-500 mb-5">
                      {t('explore.feed.cvNudgeText')}
                    </p>
                    <div className="flex gap-2 sm:gap-3">
                      <button
                        onClick={() => setShowCvNudge(false)}
                        className="flex-1 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-medium text-slate-500 border border-slate-200 hover:bg-slate-50 transition-colors"
                      >
                        {t('explore.feed.cvNudgeLater')}
                      </button>
                      <button
                        onClick={() => {
                          setShowCvNudge(false);
                          navigate('/?tab=inspire');
                        }}
                        className="flex-1 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-medium text-white bg-pink-500 hover:bg-pink-600 shadow-md transition-colors"
                      >
                        {t('explore.feed.cvNudgeButton')}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Feed content */}
          {feedLoading && currentFeed.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <PassportCoverSkeleton key={i} />
              ))}
            </div>
          ) : !isSignedIn ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center mb-4">
                <IdCard className="w-6 h-6 sm:w-7 sm:h-7 text-pink-500" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-2">{t('explore.feed.signUpTitle')}</h3>
              <p className="text-xs sm:text-sm text-slate-500 mb-5 max-w-sm leading-relaxed">{t('explore.feed.communityProof')}</p>
              <button
                onClick={() => setShowAuthModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 rounded-full shadow-sm hover:from-pink-600 hover:to-rose-600 transition-all"
              >
                {t('explore.feed.signUpButton')}
              </button>
            </div>
          ) : currentFeed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <span className="text-3xl mb-3">💫</span>
              <p className="text-sm font-medium text-slate-500">{t('explore.feed.noResults')}</p>
              <p className="text-xs text-slate-400 mt-1">{t('explore.feed.noResultsHint')}</p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentFeed.slice(0, feedDisplayCount).map((cv) => (
                <div
                  key={cv.cv_id}
                  ref={feedItemRef}
                  data-cv-id={cv.cv_id}
                >
                  {cvImages[cv.cv_id] ? (
                    <PassportCover
                      thumbnail={cvImages[cv.cv_id]?.images?.[0] || null}
                      realTalkSentence={cvRealTalk[cv.cv_id]?.real_talk_sentence || null}
                      tags={cvRealTalk[cv.cv_id]?.real_talk_tags || null}
                      stations={cvPassport[cv.cv_id]?.stations || null}
                      loading={!passportDone[cv.cv_id] || cvRealTalk[cv.cv_id] === undefined}
                      onClick={() => handleCVSelect(cv.cv_id)}
                      footerRight={
                        <>
                          <button
                            type="button"
                            aria-label="View passport"
                            onClick={(e) => { e.stopPropagation(); handleCVSelect(cv.cv_id); }}
                            className="flex items-center justify-center h-6 w-6 rounded-full border bg-white text-slate-500 border-slate-200 hover:border-pink-300 hover:text-pink-600 transition-all"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          {ENABLE_PASSPORT_REACTIONS && (
                            <PassportReactionBar cvId={cv.cv_id} apiCall={apiCall} variant="compact" initialReactions={myReactions[cv.cv_id]} onChange={(keys) => setMyReactions((prev) => ({ ...prev, [cv.cv_id]: keys }))} />
                          )}
                        </>
                      }
                    />
                  ) : (
                    <PassportCoverSkeleton />
                  )}
                </div>
              ))}

              {/* Infinite scroll sentinel - only after last visible CV is fully loaded */}
              {feedDisplayCount < currentFeed.length &&
                cvImages[currentFeed[feedDisplayCount - 1]?.cv_id] && (
                <div ref={feedSentinelRef} className="h-px col-span-full" />
              )}
            </div>

            {/* End of feed → nudge to search for more */}
            {feedDisplayCount >= currentFeed.length && (
              <div className="text-center py-8 sm:py-12 px-4">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-full shadow-sm hover:border-pink-300 hover:text-pink-600 transition-all"
                >
                  {t('explore.feed.endCta')}
                </button>
              </div>
            )}
            </>
          )}
          </>
          )}
        </div>
      ) : (
        /* ─── SEARCH VIEW ─── */
        <>
          {/* Back to feed button */}
          {allResults.length > 0 && (
            <button
              onClick={clearSearch}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-lg text-sm font-medium text-slate-600 hover:text-pink-500 hover:border-pink-300 transition-all"
            >
              <X className="w-3.5 h-3.5" />
              {t('explore.feed.backToFeed')}
            </button>
          )}

          {/* ─── Search results: passport cards + inline detail ─── */}
          <div className="flex-1 w-full max-w-4xl mx-auto px-4 py-4">
            {selectedCV ? (
              /* Inline passport detail — prev/next across the search results */
              <div
                ref={detailRef}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden scroll-mt-3 mb-28 max-w-2xl mx-auto w-full"
              >
                <PassportViewer
                  cvId={selectedCV}
                  fillHeight={false}
                  imagesOverride={cvImages[selectedCV]?.images || null}
                  passportOverride={cvPassport[selectedCV]}
                  realTalkOverride={cvRealTalk[selectedCV] ? {
                    cv_id: selectedCV,
                    real_talk_sentence: cvRealTalk[selectedCV].real_talk_sentence,
                    real_talk_tags: cvRealTalk[selectedCV].real_talk_tags,
                    is_contactable: cvRealTalk[selectedCV].is_contactable,
                  } : null}
                  score={searchResults.find(r => r.cv_id === selectedCV)?.score || 0}
                  onBack={() => {
                    if (initialCV && searchResults.length === 0) {
                      navigate('/?tab=explore');
                    } else {
                      closeDetail();
                    }
                  }}
                  isSaved={ENABLE_SAVED_PASSPORTS && savedCVIds.has(selectedCV)}
                  onToggleSave={ENABLE_SAVED_PASSPORTS ? handleToggleSave : undefined}
                  enableReactions={ENABLE_PASSPORT_REACTIONS}
                  navSlot={searchResults.length > 1 && cvIdx >= 0 ? (
                    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-white/80">
                      <button
                        onClick={() => navigateCV('prev')}
                        disabled={!hasPrevCV}
                        className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg transition-all ${hasPrevCV ? 'text-slate-600 hover:bg-slate-100 hover:text-pink-500' : 'text-slate-300 cursor-default'}`}
                      >
                        <ArrowLeft className="w-3.5 h-3.5" /> Prev
                      </button>
                      <span className="text-xs text-slate-400 font-medium">{cvIdx + 1} / {searchResults.length}</span>
                      <button
                        onClick={() => navigateCV('next')}
                        disabled={!hasNextCV}
                        className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg transition-all ${hasNextCV ? 'text-slate-600 hover:bg-slate-100 hover:text-pink-500' : 'text-slate-300 cursor-default'}`}
                      >
                        Next <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : undefined}
                />
              </div>
            ) : (
              <>
                {/* Results header + language filter */}
                {allResults.length > 0 && (
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold text-slate-700">
                      {filteredResultsCount} {t('explore.resultsList.resultsCount', 'results')}
                    </h2>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setLanguageFilter({ ...languageFilter, de: !languageFilter.de })}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${languageFilter.de ? 'bg-slate-400 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                      >🇩🇪</button>
                      <button
                        onClick={() => setLanguageFilter({ ...languageFilter, en: !languageFilter.en })}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${languageFilter.en ? 'bg-slate-400 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                      >🇬🇧</button>
                    </div>
                  </div>
                )}

                {searchResults.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {searchResults.slice(0, searchScrollCount).map((cv) => (
                      <div key={cv.cv_id} ref={searchItemRef} data-cv-id={cv.cv_id}>
                        <PassportCover
                          thumbnail={cvImages[cv.cv_id]?.images?.[0] || null}
                          realTalkSentence={cvRealTalk[cv.cv_id]?.real_talk_sentence || null}
                          tags={cvRealTalk[cv.cv_id]?.real_talk_tags || null}
                          stations={cvPassport[cv.cv_id]?.stations || null}
                          loading={!passportDone[cv.cv_id] || cvRealTalk[cv.cv_id] === undefined}
                          score={cv.score}
                          onClick={() => handleCVSelect(cv.cv_id)}
                          footerRight={
                        <>
                          <button
                            type="button"
                            aria-label="View passport"
                            onClick={(e) => { e.stopPropagation(); handleCVSelect(cv.cv_id); }}
                            className="flex items-center justify-center h-6 w-6 rounded-full border bg-white text-slate-500 border-slate-200 hover:border-pink-300 hover:text-pink-600 transition-all"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          {ENABLE_PASSPORT_REACTIONS && (
                            <PassportReactionBar cvId={cv.cv_id} apiCall={apiCall} variant="compact" initialReactions={myReactions[cv.cv_id]} onChange={(keys) => setMyReactions((prev) => ({ ...prev, [cv.cv_id]: keys }))} />
                          )}
                        </>
                      }
                        />
                      </div>
                    ))}

                    {searchScrollCount < searchResults.length && (
                      <div ref={searchSentinelRef} className="h-px col-span-full" />
                    )}

                    {searchScrollCount >= searchResults.length && filteredResultsCount > displayCount && (
                      <div className="py-2 text-center col-span-full">
                        <button
                          onClick={handleShowMore}
                          className="px-4 py-2 rounded-full text-xs font-medium text-pink-500 border border-pink-200 hover:bg-pink-50 transition-colors"
                        >
                          {filteredResultsCount - displayCount} {t('explore.results.showMore', 'more results')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : isLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                      <PassportCoverSkeleton key={i} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <span className="text-3xl mb-3">💫</span>
                    <p className="text-sm font-medium text-slate-500">{t('explore.noResults', 'No results found')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
      
      {/* Authentication Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        signupPath="/signup"
      />
    </div>
  );
};

export default CVSearch;
